import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { fetchMember, signOut as apiSignOut } from '@/lib/weddingApi';
import type { WeddingMember } from '@/types/wedding';

interface AuthContextValue {
  session: Session | null;
  member: WeddingMember | null;
  loading: boolean;
  memberLoading: boolean;
  refreshMember: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [member, setMember] = useState<WeddingMember | null>(null);
  const [loading, setLoading] = useState(true);
  const [memberLoading, setMemberLoading] = useState(false);

  const loadMember = useCallback(async (uid: string) => {
    setMemberLoading(true);
    try {
      const m = await fetchMember(uid);
      setMember(m);
    } catch {
      setMember(null);
    } finally {
      setMemberLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;

    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!active) return;
        setSession(data.session);
        setLoading(false);
        if (data.session?.user) {
          void loadMember(data.session.user.id);
        }
      })
      .catch(() => {
        if (active) setLoading(false);
      });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      if (nextSession?.user) {
        void loadMember(nextSession.user.id);
      } else {
        setMember(null);
        setMemberLoading(false);
      }
    });

    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, [loadMember]);

  const refreshMember = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    if (data.session?.user) await loadMember(data.session.user.id);
  }, [loadMember]);

  const signOut = useCallback(async () => {
    await apiSignOut();
    setMember(null);
    setSession(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ session, member, loading, memberLoading, refreshMember, signOut }),
    [session, member, loading, memberLoading, refreshMember, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}